<template>
  <div v-if="visible" class="report-modal-backdrop" @click.self="handleClose">
    <div class="report-modal">
      <div class="report-modal__hero">
        <div class="report-modal__hero-copy">
          <span class="report-modal__eyebrow">Submission</span>
          <h3>Submit an annual report</h3>
          <p>
            Submit a report with the organization’s tracking ID, title, year, and supporting file.
          </p>
        </div>
        <button
          type="button"
          class="report-modal__close"
          aria-label="Close modal"
          @click="handleClose"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <form class="report-form" novalidate @submit.prevent="handleSubmit">
        <div class="report-form__grid">
          <label class="report-field report-field--wide">
            <span>Tracking ID</span>
            <input
              v-model.trim="reportForm.tracking_id"
              :class="{ 'report-field__control--error': isFieldInvalid('tracking_id') }"
              type="text"
              placeholder="Enter the organization’s tracking ID"
              required
              aria-required="true"
              :aria-invalid="String(isFieldInvalid('tracking_id'))"
              @input="clearFieldError('tracking_id')"
            />
            <small v-if="isFieldInvalid('tracking_id')" class="report-field__error">{{
              fieldErrors.tracking_id
            }}</small>
          </label>

          <label class="report-field">
            <span>Year</span>
            <input
              v-model.trim="reportForm.year"
              :class="{ 'report-field__control--error': isFieldInvalid('year') }"
              type="number"
              min="2000"
              placeholder="2026"
              required
              aria-required="true"
              :aria-invalid="String(isFieldInvalid('year'))"
              @input="clearFieldError('year')"
            />
            <small v-if="isFieldInvalid('year')" class="report-field__error">{{
              fieldErrors.year
            }}</small>
          </label>

          <label class="report-field">
            <span>Title</span>
            <input
              v-model.trim="reportForm.title"
              :class="{ 'report-field__control--error': isFieldInvalid('title') }"
              type="text"
              placeholder="Annual impact report"
              required
              aria-required="true"
              :aria-invalid="String(isFieldInvalid('title'))"
              @input="clearFieldError('title')"
            />
            <small v-if="isFieldInvalid('title')" class="report-field__error">{{
              fieldErrors.title
            }}</small>
          </label>

          <label class="report-field report-field--wide">
            <span>Report file</span>
            <input
              :class="{ 'report-field__control--error': isFieldInvalid('report_file') }"
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              required
              aria-required="true"
              :aria-invalid="String(isFieldInvalid('report_file'))"
              @change="handleReportFile"
            />
            <small class="report-field__hint">
              Accepted file types: PDF, DOC, DOCX. Maximum size: 10 MB.
            </small>
            <small v-if="isFieldInvalid('report_file')" class="report-field__error">
              {{ fieldErrors.report_file }}
            </small>
            <small v-if="reportForm.report_file">{{ reportForm.report_file.name }}</small>
          </label>
        </div>

        <div class="report-modal__actions">
          <button type="button" class="modal-button modal-button--ghost" @click="handleClose">
            Cancel
          </button>
          <button type="submit" class="modal-button modal-button--primary" :disabled="isSubmitting">
            <b-spinner v-if="isSubmitting" small />
            <span>{{ isSubmitting ? 'Submitting...' : 'Submit report' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
const MAX_REPORT_FILE_SIZE = 10 * 1024 * 1024;

const createEmptyReportForm = () => ({
  tracking_id: '',
  year: '',
  title: '',
  report_file: null,
});

export default {
  name: 'AnnualReportModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    onSubmit: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      reportForm: createEmptyReportForm(),
      fieldErrors: {},
    };
  },

  watch: {
    visible(isVisible) {
      if (!isVisible) {
        this.reportForm = createEmptyReportForm();
        this.fieldErrors = {};
      }
    },
  },

  methods: {
    isFieldInvalid(field) {
      return Boolean(this.fieldErrors[field]);
    },

    clearFieldError(field) {
      if (!this.fieldErrors[field]) {
        return;
      }

      this.$delete(this.fieldErrors, field);
    },

    handleClose() {
      if (this.isSubmitting) {
        return;
      }

      this.$emit('close');
    },

    handleReportFile(event) {
      const [file] = event.target.files || [];
      const input = event.target;
      if (!file) {
        this.reportForm.report_file = null;
        this.fieldErrors.report_file = 'Report file is required';
        return;
      }

      const allowedMimeTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      const allowedExtensions = ['pdf', 'doc', 'docx'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      const hasAllowedMimeType = allowedMimeTypes.includes(file.type);
      const hasAllowedExtension = allowedExtensions.includes(fileExtension);

      if (!hasAllowedMimeType && !hasAllowedExtension) {
        this.reportForm.report_file = null;
        input.value = '';
        this.fieldErrors.report_file = 'Upload a Word or PDF document only';
        this.$toast.show({
          type: 'ERROR',
          message: 'Upload a Word or PDF document only',
        });
        return;
      }

      if (file.size > MAX_REPORT_FILE_SIZE) {
        this.reportForm.report_file = null;
        input.value = '';
        this.fieldErrors.report_file = 'Upload a file no larger than 10 MB';
        this.$toast.show({
          type: 'ERROR',
          message: 'Upload a file no larger than 10 MB',
        });
        return;
      }

      this.reportForm.report_file = file;
      this.clearFieldError('report_file');
    },

    validateReportForm() {
      const requiredFields = {
        tracking_id: 'Tracking ID is required',
        year: 'Year is required',
        title: 'Title is required',
        report_file: 'Report file is required',
      };
      const nextErrors = {};

      Object.entries(requiredFields).forEach(([field, message]) => {
        const value = this.reportForm[field];
        const isEmpty = field === 'report_file' ? !value : !String(value || '').trim();

        if (isEmpty) {
          nextErrors[field] = message;
        }
      });

      this.fieldErrors = nextErrors;

      return !Object.keys(nextErrors).length;
    },

    handleSubmit() {
      if (this.isSubmitting) return;
      if (!this.validateReportForm()) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Please complete all report submission fields',
        });
        return;
      }

      const payload = { ...this.reportForm };

      if (typeof this.onSubmit === 'function') {
        this.onSubmit(payload);
      }

      this.$emit('submit-report', payload);
    },
  },
};
</script>

<style scoped>
.report-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(25, 20, 48, 0.34);
  backdrop-filter: blur(6px);
}

.report-modal {
  width: min(100%, 760px);
  max-height: calc(100vh - 1.5rem);
  overflow: auto;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(247, 246, 252, 0.96));
  border: 1px solid rgba(48, 42, 82, 0.08);
  box-shadow: 0 24px 60px rgba(38, 34, 66, 0.22);
}

.report-modal__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.45rem 1.45rem 1rem;
  border-bottom: 1px solid rgba(48, 42, 82, 0.08);
}

.report-modal__eyebrow {
  display: inline-block;
  margin-bottom: 0.55rem;
  color: #5f4fb1;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.report-modal__hero-copy h3 {
  margin: 0;
  color: #31285d;
  font-size: 1.55rem;
}

.report-modal__hero-copy p {
  margin: 0.7rem 0 0;
  color: #6d6884;
  line-height: 1.6;
}

.report-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(48, 42, 82, 0.08);
  color: #4f4870;
}

.report-form {
  padding: 1.35rem 1.45rem 1.45rem;
}

.report-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.report-field {
  display: grid;
  gap: 0.5rem;
}

.report-field--wide {
  grid-column: 1 / -1;
}

.report-field span {
  color: #4f4870;
  font-size: 0.9rem;
  font-weight: 700;
}

.report-field input {
  width: 100%;
  border: 1px solid rgba(48, 42, 82, 0.12);
  border-radius: 18px;
  padding: 0.9rem 1rem;
  background: #faf9fe;
  color: #31285d;
  outline: none;
}

.report-field__control--error {
  border-color: #d64545 !important;
  background: #fff6f6 !important;
  box-shadow: 0 0 0 3px rgba(214, 69, 69, 0.12);
}

.report-field small {
  color: #6d6884;
}

.report-field__error {
  color: #c23a3a !important;
  font-weight: 600;
}

.report-field__hint {
  color: #6d6884;
}

.report-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  border-radius: 999px;
  padding: 0.82rem 1.15rem;
  font-weight: 700;
  width: fit-content;
}

.modal-button--ghost {
  background: rgba(48, 42, 82, 0.08);
  color: #4f4870;
}

.modal-button--primary {
  background: linear-gradient(135deg, #5f4fb1, #7f72d5);
  color: #ffffff;
}

@media (max-width: 991px) {
  .report-modal__hero,
  .report-modal__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .report-form__grid {
    grid-template-columns: 1fr;
  }
}
</style>
